import { Image, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useEffect, useRef, useState } from 'react';
import { Colors, SportColors } from '@/constants/colors';
import { AnyGame, FootballGame, GameStatistic, GameTimelineEvent, NBAGame, NFLGame, Sport } from '@/types';
import { getFootballGameDetail } from '@/services/api/football';
import { getNBAGameDetail } from '@/services/api/nba';
import { getNFLGameDetail } from '@/services/api/nfl';
import { find365FootballGameDetailForMatch, get365FootballGameDetail } from '@/services/api/scores365';
import { getESPNFootballGameDetail } from '@/services/api/espn-all';
import { getSofaMatchStatistics, TimelineItem } from '@/services/api/sofascore';
import { LiveBadge } from '@/components/LiveBadge';
import { useFavoritesStore } from '@/store/favorites';
import { gameCache } from '@/store/gameCache';
import { useMatchIncidents } from '@/hooks/useMatchIncidents';
import { sameFootballMatch } from '@/utils/teamMatching';
import { getMainStatistics } from '@/utils/statistics';

function timelineIcon(type: GameTimelineEvent['type']): string {
  switch (type) {
    case 'game_start':
    case 'period_start':
      return '>';
    case 'halftime':
    case 'period_end':
      return 'HT';
    case 'game_end':
      return 'FT';
    case 'goal':
      return 'GOL';
    case 'card':
      return 'CARD';
    case 'substitution':
      return 'SUB';
    case 'touchdown':
      return 'TD';
    case 'field_goal':
      return 'FG';
    case 'safety':
      return 'SF';
    case 'score':
    case 'extra_point':
      return '+';
    default:
      return '-';
  }
}

function sofaTimelineToGameTimeline(items: TimelineItem[]): GameTimelineEvent[] {
  return items.map((item) => ({
    id: String(item.id),
    side: item.side,
    minute: item.minute,
    type: item.side === 'center' ? 'period_start' : 'other',
    title: item.text,
    detail: item.detail,
  }));
}

function scoreLabel(event: GameTimelineEvent): string | undefined {
  if (!event.score) return undefined;
  return `${event.score.home} - ${event.score.away}`;
}

function timelineMinuteOrder(value?: string): number {
  const match = String(value ?? '').replace("'", '').trim().match(/^(\d+)(?:\+(\d+))?/);
  if (!match) return 999;
  return Number(match[1]) + (match[2] ? Number(match[2]) / 100 : 0);
}

function timelineOrder(event: GameTimelineEvent): number {
  if (typeof event.sortOrder === 'number') return event.sortOrder;
  if (event.type === 'game_start') return 0;
  if (event.type === 'halftime' || event.type === 'period_end') return 45.9;
  if (event.type === 'period_start') return 45.95;
  if (event.type === 'game_end') return 130;
  return timelineMinuteOrder(event.minute ?? event.period);
}

function sortTimeline(items: GameTimelineEvent[]): GameTimelineEvent[] {
  return [...items].sort((a, b) => timelineOrder(b) - timelineOrder(a));
}

function statPercent(stat: GameStatistic, side: 'home' | 'away'): number | undefined {
  const home = stat.homeValue;
  const away = stat.awayValue;
  if (home == null || away == null) return undefined;
  const total = home + away;
  if (!Number.isFinite(total) || total <= 0) return undefined;
  return side === 'home' ? home / total : away / total;
}

function sportTitle(sport: Sport, game: AnyGame): string {
  if (sport === 'nba') return 'NBA';
  if (sport === 'nfl') return 'NFL';
  const football = game as FootballGame;
  return football.league?.name ?? 'Futebol';
}

function hasFootballEvents(game: FootballGame | null | undefined): boolean {
  return (
    (game?.events?.length ?? 0) > 0 ||
    (game?.timeline ?? []).some((event) => event.side !== 'center')
  );
}

function hasFootballStatistics(game: FootballGame | null | undefined): boolean {
  return (game?.statistics?.length ?? 0) > 0;
}

function shouldTry365Detail(game: FootballGame | null | undefined): boolean {
  return game?.status === 'live' || game?.status === 'halftime' || game?.status === 'finished';
}

function canReplaceGame(next: AnyGame, current: AnyGame | null): boolean {
  if (!current) return true;
  if (next.sport !== current.sport) return false;
  if (next.sport === 'futebol' && current.sport === 'futebol') {
    return sameFootballMatch(next as FootballGame, current as FootballGame);
  }
  return next.homeTeam.id === current.homeTeam.id && next.awayTeam.id === current.awayTeam.id;
}

export default function MatchDetailScreen() {
  const { id, sport } = useLocalSearchParams() as { id?: string; sport?: Sport };
  const router = useRouter();
  const [game, setGame] = useState<AnyGame | null>(
    () => (sport && id ? gameCache.get(sport, id) ?? null : null)
  );
  const gameRef = useRef<AnyGame | null>(game);
  const [loading, setLoading] = useState(!gameCache.has(sport ?? '', id ?? ''));
  const [sofaStatistics, setSofaStatistics] = useState<GameStatistic[]>([]);
  const { isFavorite, addFavorite, removeFavorite } = useFavoritesStore();

  const sportKey: Sport = sport ?? 'futebol';
  const sportColor = SportColors[sportKey];
  const gameIsLive = game?.status === 'live' || game?.status === 'halftime';
  const isSofaGame =
    sport === 'futebol' && !!(game as FootballGame | null)?.league?.id?.startsWith('sofa-');
  const sofaEventId = isSofaGame ? id ?? null : null;
  const { items: sofaTimeline } = useMatchIncidents(sofaEventId, !!gameIsLive);

  useEffect(() => {
    gameRef.current = game;
  }, [game]);

  useEffect(() => {
    if (!id || !sport) return;
    const gameId = id;
    const gameSport = sport;

    async function refresh() {
      try {
        let data: AnyGame | null = null;
        const currentGame = gameRef.current;
        if (sport === 'futebol') {
          const cachedLeagueId = (currentGame as FootballGame | null)?.league?.id;
          if (gameId.startsWith('365:')) {
            data = await get365FootballGameDetail(gameId);
          } else if (gameId.startsWith('espn-')) {
            data = await getESPNFootballGameDetail(gameId, cachedLeagueId);
          } else {
            data = await getFootballGameDetail(gameId);
          }

          const footballData = data as FootballGame | null;
          const currentFootball = currentGame?.sport === 'futebol' ? (currentGame as FootballGame) : null;
          const fallbackTarget = footballData ?? currentFootball;
          const needs365Detail =
            fallbackTarget &&
            (
              shouldTry365Detail(fallbackTarget) ||
              !hasFootballEvents(footballData) ||
              !hasFootballStatistics(footballData)
            );

          if (needs365Detail) {
            const from365 = await find365FootballGameDetailForMatch(fallbackTarget);
            if (
              from365 &&
              (
                shouldTry365Detail(from365) ||
                hasFootballEvents(from365) ||
                hasFootballStatistics(from365) ||
                !hasFootballEvents(footballData)
              )
            ) {
              data = {
                ...from365,
                events: from365.events?.length ? from365.events : footballData?.events,
                timeline: from365.timeline?.length ? from365.timeline : footballData?.timeline,
                statistics: from365.statistics?.length ? from365.statistics : footballData?.statistics,
              };
            }
          }
        }
        if (sport === 'nba') data = await getNBAGameDetail(gameId);
        if (sport === 'nfl') data = await getNFLGameDetail(gameId);

        if (data) {
          setGame((current) => {
            if (!canReplaceGame(data!, current)) return current;
            gameCache.set(data!);
            gameCache.setForKey(gameSport, gameId, data!);
            return data;
          });
        }
      } catch {
        // Keep cached data when an upstream feed misses a detail endpoint.
      } finally {
        setLoading(false);
      }
    }

    refresh();
    const timer = setInterval(refresh, 30_000);
    return () => clearInterval(timer);
  }, [id, sport]);

  useEffect(() => {
    let active = true;
    async function loadSofaStatistics() {
      if (!sofaEventId) {
        setSofaStatistics([]);
        return;
      }
      try {
        const stats = await getSofaMatchStatistics(Number(sofaEventId));
        if (active) setSofaStatistics(stats);
      } catch {
        if (active) setSofaStatistics([]);
      }
    }

    loadSofaStatistics();
    if (!gameIsLive || !sofaEventId) return () => { active = false; };
    const timer = setInterval(loadSofaStatistics, 30_000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [gameIsLive, sofaEventId]);

  if (loading || !game) {
    return (
      <SafeAreaView style={styles.safe}>
        <Text style={styles.loadingText}>Carregando...</Text>
      </SafeAreaView>
    );
  }

  const isLive = game.status === 'live' || game.status === 'halftime';
  const footballGame = sport === 'futebol' ? (game as FootballGame) : null;
  const nbaGame = sport === 'nba' ? (game as NBAGame) : null;
  const nflGame = sport === 'nfl' ? (game as NFLGame) : null;
  const favHome = isFavorite(game.homeTeam.id, game.sport);
  const favAway = isFavorite(game.awayTeam.id, game.sport);
  const detailTimeline = sortTimeline(
    sofaTimeline.length > 0
      ? sofaTimelineToGameTimeline(sofaTimeline)
      : game.timeline ?? []
  );
  const detailStatistics = getMainStatistics(
    sofaStatistics.length > 0 ? sofaStatistics : game.statistics ?? [],
    sportKey
  );

  function toggleFav(teamId: string, teamName: string, logo: string, isHome: boolean) {
    const fav = isHome ? favHome : favAway;
    if (fav) {
      removeFavorite(teamId, game!.sport);
    } else {
      addFavorite({ id: teamId, sport: game!.sport, name: teamName, logo });
    }
  }

  return (
    <SafeAreaView style={styles.safe} edges={['top']}>
      <View style={styles.nav}>
        <TouchableOpacity onPress={() => router.back()} style={styles.backBtn}>
          <Text style={styles.backText}>Voltar</Text>
        </TouchableOpacity>
        <View style={styles.leagueInfo}>
          {footballGame?.league.logo ? (
            <Image source={{ uri: footballGame.league.logo }} style={styles.leagueLogo} resizeMode="contain" />
          ) : null}
          <Text style={styles.leagueName}>{sportTitle(game.sport, game)}</Text>
        </View>
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.statusRow}>
          {isLive ? (
            <LiveBadge
              sport={sport}
              minute={footballGame?.minute}
              period={nbaGame?.period ?? nflGame?.quarter}
              clock={nbaGame?.clock ?? nflGame?.clock}
              isHalftime={game.status === 'halftime'}
            />
          ) : (
            <Text style={styles.statusText}>
              {game.status === 'finished' ? 'Encerrado' : new Date(game.startTime).toLocaleString('pt-BR')}
            </Text>
          )}
        </View>

        <View style={styles.scoreSection}>
          <TouchableOpacity style={styles.teamCol} onPress={() => toggleFav(game.homeTeam.id, game.homeTeam.name, game.homeTeam.logo, true)}>
            <Image source={{ uri: game.homeTeam.logo }} style={styles.bigLogo} resizeMode="contain" />
            <Text style={[styles.teamFullName, favHome && { color: sportColor.primary }]}>{game.homeTeam.name}</Text>
            <Text style={styles.favHint}>{favHome ? 'Favorito' : 'Favoritar'}</Text>
          </TouchableOpacity>

          <View style={styles.mainScore}>
            <Text style={styles.bigScore}>{game.score.home}</Text>
            <Text style={styles.scoreSep}>-</Text>
            <Text style={styles.bigScore}>{game.score.away}</Text>
          </View>

          <TouchableOpacity style={styles.teamCol} onPress={() => toggleFav(game.awayTeam.id, game.awayTeam.name, game.awayTeam.logo, false)}>
            <Image source={{ uri: game.awayTeam.logo }} style={styles.bigLogo} resizeMode="contain" />
            <Text style={[styles.teamFullName, favAway && { color: sportColor.primary }]}>{game.awayTeam.name}</Text>
            <Text style={styles.favHint}>{favAway ? 'Favorito' : 'Favoritar'}</Text>
          </TouchableOpacity>
        </View>

        {nflGame && isLive && nflGame.down && (
          <View style={styles.nflSituation}>
            <Text style={styles.nflSituationText}>
              {nflGame.down} tentativa · {nflGame.yardsToGo} jardas para o first down
            </Text>
            <Text style={styles.nflPossession}>
              Posse: {nflGame.possession === 'home' ? game.homeTeam.shortName : game.awayTeam.shortName}
            </Text>
          </View>
        )}

        <View style={styles.eventsSection}>
          <Text style={styles.eventsTitle}>EVENTOS DA PARTIDA{gameIsLive ? '  AO VIVO' : ''}</Text>
          {detailTimeline.length > 0 ? (
            detailTimeline.map((event) => {
              if (event.side === 'center') {
                return (
                  <View key={event.id} style={styles.timelineCenterRow}>
                    <View style={[styles.timelineCenterPill, { borderColor: sportColor.primary }]}>
                      <Text style={styles.timelineCenterText}>
                        {timelineIcon(event.type)} {event.title}{event.detail ? ` · ${event.detail}` : ''}
                      </Text>
                      {scoreLabel(event) ? <Text style={styles.timelineCenterScore}>{scoreLabel(event)}</Text> : null}
                    </View>
                  </View>
                );
              }

              const away = event.side === 'away';
              const team = away ? game.awayTeam : game.homeTeam;
              return (
                <View key={event.id} style={styles.timelineRow}>
                  <View style={styles.timelineTeamSide}>
                    {!away && (
                      <View style={styles.timelineCard}>
                        <View style={styles.timelineCardHeader}>
                          <Image source={{ uri: team.logo }} style={styles.timelineLogo} resizeMode="contain" />
                          <Text style={styles.timelineTeamName}>{team.shortName}</Text>
                        </View>
                        <Text style={styles.timelineTitle}>{event.title}</Text>
                        {event.detail ? <Text style={styles.timelineDetail}>{event.detail}</Text> : null}
                      </View>
                    )}
                  </View>

                  <View style={styles.timelineAxis}>
                    <Text style={styles.timelineMinute}>{event.minute ?? event.period ?? ''}</Text>
                    <View style={[styles.timelineDot, { borderColor: sportColor.primary }]}>
                      <Text style={[styles.timelineIconText, { color: sportColor.primary }]}>
                        {timelineIcon(event.type)}
                      </Text>
                    </View>
                    {scoreLabel(event) ? <Text style={styles.timelineScore}>{scoreLabel(event)}</Text> : null}
                  </View>

                  <View style={styles.timelineTeamSide}>
                    {away && (
                      <View style={styles.timelineCard}>
                        <View style={styles.timelineCardHeader}>
                          <Image source={{ uri: team.logo }} style={styles.timelineLogo} resizeMode="contain" />
                          <Text style={styles.timelineTeamName}>{team.shortName}</Text>
                        </View>
                        <Text style={styles.timelineTitle}>{event.title}</Text>
                        {event.detail ? <Text style={styles.timelineDetail}>{event.detail}</Text> : null}
                      </View>
                    )}
                  </View>
                </View>
              );
            })
          ) : (
            <Text style={styles.emptyInline}>Eventos aparecerão aqui assim que a partida tiver lances disponíveis.</Text>
          )}
        </View>

        <View style={styles.statsSection}>
          <Text style={styles.eventsTitle}>PRINCIPAIS ESTATÍSTICAS</Text>
          {detailStatistics.length > 0 ? (
            <View style={styles.statsCard}>
              <View style={styles.statsTeams}>
                <Text style={styles.statsTeamName}>{game.homeTeam.shortName}</Text>
                <Text style={styles.statsTeamName}>{game.awayTeam.shortName}</Text>
              </View>
              {detailStatistics.map((stat) => {
                const homeWidth = statPercent(stat, 'home');
                const awayWidth = statPercent(stat, 'away');
                return (
                  <View key={stat.id} style={styles.statRow}>
                    <View style={styles.statValues}>
                      <Text style={styles.statValue}>{stat.home}</Text>
                      <Text style={styles.statLabel}>{stat.label}</Text>
                      <Text style={styles.statValue}>{stat.away}</Text>
                    </View>
                    {homeWidth != null && awayWidth != null ? (
                      <View style={styles.statBar}>
                        <View style={[styles.statBarHome, { flex: homeWidth, backgroundColor: sportColor.primary }]} />
                        <View style={styles.statBarGap} />
                        <View style={[styles.statBarAway, { flex: awayWidth }]} />
                      </View>
                    ) : null}
                  </View>
                );
              })}
            </View>
          ) : (
            <Text style={styles.emptyInline}>Estatísticas aparecem quando a fonte do jogo liberar os dados.</Text>
          )}
        </View>

        {nbaGame && nbaGame.quarter_scores && nbaGame.quarter_scores.length > 0 && (
          <View style={styles.quartersSection}>
            <Text style={styles.eventsTitle}>PARCIAIS</Text>
            <View style={styles.quartersTable}>
              <View style={styles.quarterHeader}>
                <Text style={styles.quarterHeaderCell}>Time</Text>
                {nbaGame.quarter_scores.map((q) => (
                  <Text key={q.period} style={styles.quarterHeaderCell}>Q{q.period}</Text>
                ))}
                <Text style={styles.quarterHeaderCell}>Total</Text>
              </View>
              <View style={styles.quarterRow}>
                <Text style={styles.quarterTeam}>{game.homeTeam.shortName}</Text>
                {nbaGame.quarter_scores.map((q) => (
                  <Text key={q.period} style={styles.quarterCell}>{q.home}</Text>
                ))}
                <Text style={[styles.quarterCell, styles.quarterTotal]}>{game.score.home}</Text>
              </View>
              <View style={styles.quarterRow}>
                <Text style={styles.quarterTeam}>{game.awayTeam.shortName}</Text>
                {nbaGame.quarter_scores.map((q) => (
                  <Text key={q.period} style={styles.quarterCell}>{q.away}</Text>
                ))}
                <Text style={[styles.quarterCell, styles.quarterTotal]}>{game.score.away}</Text>
              </View>
            </View>
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: Colors.background },
  nav: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    gap: 12,
  },
  backBtn: { paddingVertical: 6, paddingRight: 10 },
  backText: { color: Colors.textSecondary, fontSize: 14, fontWeight: '700' },
  leagueInfo: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 },
  leagueLogo: { width: 20, height: 20 },
  leagueName: { flex: 1, color: Colors.textSecondary, fontSize: 13, fontWeight: '700' },
  content: { paddingBottom: 44 },
  statusRow: { alignItems: 'center', paddingVertical: 12 },
  statusText: { color: Colors.textSecondary, fontSize: 13 },
  scoreSection: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 24,
    gap: 8,
  },
  teamCol: { flex: 1, alignItems: 'center', gap: 8 },
  bigLogo: { width: 72, height: 72 },
  teamFullName: { color: Colors.text, fontSize: 13, fontWeight: '700', textAlign: 'center' },
  favHint: { color: Colors.textMuted, fontSize: 11 },
  mainScore: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  bigScore: { color: Colors.text, fontSize: 48, fontWeight: '900', fontVariant: ['tabular-nums'] },
  scoreSep: { color: Colors.textMuted, fontSize: 32 },
  nflSituation: {
    alignItems: 'center',
    backgroundColor: Colors.surface,
    marginHorizontal: 16,
    borderRadius: 10,
    padding: 14,
    gap: 4,
  },
  nflSituationText: { color: Colors.text, fontSize: 14, fontWeight: '700', textAlign: 'center' },
  nflPossession: { color: Colors.textSecondary, fontSize: 12 },
  eventsSection: { marginTop: 24, paddingHorizontal: 16 },
  eventsTitle: {
    color: Colors.textMuted,
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1,
    marginBottom: 12,
  },
  timelineCenterRow: { alignItems: 'center', paddingVertical: 10 },
  timelineCenterPill: {
    backgroundColor: Colors.surfaceAlt,
    paddingHorizontal: 13,
    paddingVertical: 7,
    borderRadius: 18,
    borderWidth: 1,
  },
  timelineCenterText: { color: Colors.text, fontSize: 12, fontWeight: '800', textAlign: 'center' },
  timelineCenterScore: { color: Colors.textSecondary, fontSize: 11, fontWeight: '900', textAlign: 'center', marginTop: 2 },
  timelineRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    minHeight: 78,
    paddingVertical: 8,
  },
  timelineTeamSide: { flex: 1, justifyContent: 'center' },
  timelineAxis: { width: 62, alignItems: 'center', justifyContent: 'center' },
  timelineMinute: { color: Colors.textMuted, fontSize: 11, minHeight: 14, fontVariant: ['tabular-nums'] },
  timelineDot: {
    width: 38,
    minHeight: 30,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.surface,
    marginVertical: 4,
    paddingHorizontal: 3,
  },
  timelineIconText: { fontSize: 10, fontWeight: '900', textAlign: 'center' },
  timelineScore: { color: Colors.text, fontSize: 11, fontWeight: '800', fontVariant: ['tabular-nums'] },
  timelineCard: {
    backgroundColor: Colors.surface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.border,
    padding: 10,
    gap: 5,
  },
  timelineCardHeader: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  timelineLogo: { width: 18, height: 18 },
  timelineTeamName: { color: Colors.textMuted, fontSize: 11, fontWeight: '800' },
  timelineTitle: { color: Colors.text, fontSize: 13, fontWeight: '800' },
  timelineDetail: { color: Colors.textSecondary, fontSize: 11, lineHeight: 15 },
  statsSection: { marginTop: 24, paddingHorizontal: 16 },
  statsCard: {
    backgroundColor: Colors.surface,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: Colors.border,
    padding: 12,
  },
  statsTeams: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 },
  statsTeamName: { color: Colors.text, fontSize: 12, fontWeight: '900' },
  statRow: { paddingVertical: 9, borderTopWidth: 1, borderTopColor: Colors.border },
  statValues: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  statValue: { width: 54, color: Colors.text, fontSize: 13, fontWeight: '800', textAlign: 'center' },
  statLabel: { flex: 1, color: Colors.textSecondary, fontSize: 12, fontWeight: '700', textAlign: 'center' },
  statBar: { flexDirection: 'row', height: 4, marginTop: 7, borderRadius: 4, overflow: 'hidden', backgroundColor: Colors.surfaceAlt },
  statBarHome: { height: 4 },
  statBarAway: { height: 4, backgroundColor: Colors.textMuted },
  statBarGap: { width: 2, backgroundColor: Colors.background },
  emptyInline: {
    color: Colors.textSecondary,
    backgroundColor: Colors.surface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.border,
    padding: 12,
    fontSize: 12,
    lineHeight: 18,
  },
  quartersSection: { marginTop: 24, paddingHorizontal: 16 },
  quartersTable: { backgroundColor: Colors.surface, borderRadius: 10, overflow: 'hidden' },
  quarterHeader: {
    flexDirection: 'row',
    backgroundColor: Colors.surfaceAlt,
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  quarterHeaderCell: { color: Colors.textMuted, fontSize: 11, fontWeight: '800', flex: 1, textAlign: 'center' },
  quarterRow: {
    flexDirection: 'row',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderTopWidth: 1,
    borderTopColor: Colors.border,
  },
  quarterTeam: { color: Colors.text, fontSize: 12, fontWeight: '800', flex: 1, textAlign: 'center' },
  quarterCell: { color: Colors.textSecondary, fontSize: 12, flex: 1, textAlign: 'center' },
  quarterTotal: { color: Colors.text, fontWeight: '900' },
  loadingText: { color: Colors.textSecondary, textAlign: 'center', marginTop: 40, fontSize: 14 },
});
